// ===== Packages =====

import React                                    from 'react';
import styled                                   from 'styled-components';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    HIDDEN_VOID_CONTENT_STYLE,
    READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER,
    READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER,
}                                               from '../../../constants/generalConstants';
import FONT_TYPE                                from '../../../constants/fontType';
import { buildHeaderOneDisplaySegments }        from './headerOneDisplay';

// ===== Styles =====

import { basicEditorFontStyles }                from '../../../styles';

interface Props {
    isFirstElement: boolean,
    readOnly: boolean,
    paragraphId?: string,
    plainText: string,
    children: React.ReactNode,
    attributes: React.HTMLAttributes<HTMLElement>,
}

HeaderOne.defaultProps = {
    paragraphId: undefined,
};
/**
 * Renders a reader-style h1 that shares the centered, editorial tone of the
 * HeaderTwo part marker without repeating its exact structure.
 */
function HeaderOne({
    isFirstElement = false,
    readOnly,
    paragraphId,
    plainText,
    children,
    attributes,
}: Props): JSX.Element {
    const displaySegments = buildHeaderOneDisplaySegments(plainText);
    const shouldUseReadOnlyDisplay = readOnly && displaySegments.primaryWords.length > 0;
    const isSingleWordReadOnlyDisplay = shouldUseReadOnlyDisplay
        && displaySegments.primaryWords.length === 1
        && !displaySegments.secondaryText;
    /**
     * The split read-only heading is decorative UI. Prevent native selection so
     * Slate never tries to resolve a caret inside the remapped display words.
     */
    const preventReadOnlyDisplaySelection = (event: React.SyntheticEvent<HTMLSpanElement>): void => {
        event.preventDefault();
    };
    const normalizedTargetLabel = plainText.replace(/\s+/g, ' ').trim();

    return (
        <Container
            {...attributes}
            isFirstElement={isFirstElement}
            {...(paragraphId
                ? {
                    'data-post-link-target-id': paragraphId,
                }
                : {}
            )}
            {...(normalizedTargetLabel
                ? {
                    'data-post-link-target-label': normalizedTargetLabel,
                }
                : {}
            )}
        >
            <HeadingTextGroup>
                {shouldUseReadOnlyDisplay ? (
                    <>
                        <HiddenSlateHeadingContent>
                            {children}
                        </HiddenSlateHeadingContent>
                        <ReadOnlyHeadingLayout
                            aria-hidden="true"
                            contentEditable={false}
                            suppressContentEditableWarning
                            data-testid="post-editor-header-one-display"
                            data-display-mode={isSingleWordReadOnlyDisplay ? 'single-word' : 'split'}
                            $centerAlign={displaySegments.centerAlign || isSingleWordReadOnlyDisplay}
                            onMouseDown={preventReadOnlyDisplaySelection}
                            onTouchStart={preventReadOnlyDisplaySelection}
                        >
                            <HeadingPrimary
                                data-testid="post-editor-header-one-primary"
                                $centerAlign={displaySegments.centerAlign || isSingleWordReadOnlyDisplay}
                            >
                                {displaySegments.primaryWords.map((displayWord, index) => {
                                    const displayWordKey = displaySegments.primaryWords
                                        .slice(0, index + 1)
                                        .map(({ text }) => text)
                                        .join(' ');

                                    return (
                                        <HeadingDisplayWord
                                            key={displayWordKey}
                                            highlighted={displayWord.highlighted}
                                            data-testid={displayWord.highlighted
                                                ? 'post-editor-header-one-highlight'
                                                : undefined}
                                        >
                                            {displayWord.text}
                                        </HeadingDisplayWord>
                                    );
                                })}
                            </HeadingPrimary>
                            {!!displaySegments.secondaryText && (
                                <HeadingSecondaryLockup
                                    $centerAlign={displaySegments.centerAlign || isSingleWordReadOnlyDisplay}
                                >
                                    <HeadingSecondary
                                        data-testid="post-editor-header-one-secondary"
                                        $centerAlign={displaySegments.centerAlign || isSingleWordReadOnlyDisplay}
                                    >
                                        {displaySegments.secondaryText}
                                    </HeadingSecondary>
                                </HeadingSecondaryLockup>
                            )}
                        </ReadOnlyHeadingLayout>
                    </>
                ) : (
                    <HeadingText>
                        {children}
                    </HeadingText>
                )}
            </HeadingTextGroup>
        </Container>
    );
}

// ===== Styled Components =====

interface ContainerProps {
    isFirstElement: boolean,
}
const Container = styled.h1<ContainerProps>`
    ${basicEditorFontStyles}
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    width: calc(100% + clamp(5rem, 18vw, 16rem));
    max-width: none;
    margin: 0;
    margin-left: calc(-0.5 * clamp(5rem, 18vw, 16rem));
    margin-right: calc(-0.5 * clamp(5rem, 18vw, 16rem));
    margin-top: ${({ isFirstElement }) => (isFirstElement ? '0' : '1.4em')};
    scroll-margin-top: ${`${3.5 * BODY_FONT_SIZE}px`};
    font-size: ${`${(READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER) ** 2 * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER}em`};
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.5;
    text-align: center;
    color: ${({ theme }) => theme.color.textBlack};

    &::after {
        content: '';
        display: block;
        width: 5.5rem;
        height: 5px;
        margin: 1rem auto 0;
        background: ${({ theme }) => theme.color.cream300};
        opacity: 1;
    }

    ${({ theme }) => theme.mediaQuery.small} {
        width: 100%;
        max-width: 100%;
        margin-left: 0;
        margin-right: 0;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: 100%;
        max-width: 100%;
        font-size: ${`${(READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER) ** 2 * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER}em`};
        margin-left: 0;
        margin-right: 0;
        letter-spacing: -0.02em;

        &::after {
            width: 4rem;
            margin-top: 0.8rem;
        }
    }
`;

const HeadingTextGroup = styled.span`
    position: relative;
    display: inline-flex;
    justify-content: flex-start;
    width: 100%;
    padding: 0.2em 0.6em 0.15em;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding-left: 0.2em;
        padding-right: 0.2em;
    }
`;

interface ReadOnlyHeadingLayoutProps {
    $centerAlign: boolean,
}

const ReadOnlyHeadingLayout = styled.span<ReadOnlyHeadingLayoutProps>`
    position: relative;
    z-index: 1;
    display: inline-flex;
    flex-direction: column;
    align-items: ${({ $centerAlign }) => ($centerAlign ? 'center' : 'flex-start')};
    width: 100%;
    margin-left: ${({ $centerAlign }) => ($centerAlign ? '0' : 'clamp(0.4rem, 2.2vw, 1.8rem)')};
    text-align: ${({ $centerAlign }) => ($centerAlign ? 'center' : 'left')};
    user-select: none;
    -webkit-user-select: none;

    ${({ theme }) => theme.mediaQuery.small} {
        align-items: center;
        margin-left: 0;
        text-align: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        align-items: center;
        margin-left: 0;
        text-align: center;
    }
`;

const HeadingText = styled.span`
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    text-align: center;
`;

const HiddenSlateHeadingContent = styled.span.attrs({
    style: HIDDEN_VOID_CONTENT_STYLE,
})``;

interface HeadingPrimaryProps {
    $centerAlign: boolean,
}

const HeadingPrimary = styled.span<HeadingPrimaryProps>`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: ${({ $centerAlign }) => ($centerAlign ? 'center' : 'flex-start')};
    gap: 0 0.14em;
    max-width: none;
    font-size: 1.28em;
    font-weight: 900;
    line-height: 1.2;
    letter-spacing: -0.06em;
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        justify-content: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 0.8em;
        line-height: 0.98;
        justify-content: center;
    }
`;

interface HeadingDisplayWordProps {
    highlighted: boolean,
}

const HeadingDisplayWord = styled.span<HeadingDisplayWordProps>`
    display: inline-block;
    color: ${({ highlighted, theme }) => (highlighted
        ? theme.color.cream300
        : theme.color.textBlack)};
`;

interface HeadingSecondaryLockupProps {
    $centerAlign: boolean,
}

const HeadingSecondaryLockup = styled.span<HeadingSecondaryLockupProps>`
    display: inline-flex;
    flex-direction: column;
    align-self: ${({ $centerAlign }) => ($centerAlign ? 'center' : 'flex-start')};
    width: min(100%, 25ch);
    margin-top: 0.8rem;
    margin-left: ${({ $centerAlign }) => ($centerAlign ? '0' : 'clamp(2.5rem, 14%, 8rem)')};

    ${({ theme }) => theme.mediaQuery.small} {
        align-self: center;
        width: min(100%, 25ch);
        margin-left: 0;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        margin-top: 0.55rem;
        align-self: center;
        width: min(100%, 23ch);
        margin-left: 0;
    }
`;

interface HeadingSecondaryProps {
    $centerAlign: boolean,
}

const HeadingSecondary = styled.span<HeadingSecondaryProps>`
    display: block;
    font-size: 0.42em;
    font-weight: 800;
    letter-spacing: 0.1em;
    line-height: 1.18;
    text-align: ${({ $centerAlign }) => ($centerAlign ? 'center' : 'left')};
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        text-align: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 0.39em;
        letter-spacing: 0.08em;
        text-align: center;
    }
`;

export default HeaderOne;
